import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import { TextField } from '../../../../../../../../components/TextField';
import { SelectField } from '../../../../../../../../components/SelectField';
import { PlainRenderElementsWrapper } from './ruleEditorViewParts/PlainRenderInputElement';

// Contexts
import { CriteriaStoreContext } from '../../../../../../state/criteria/context';
import { RuleEditorContext } from '../context';

// Enums
import { dtoState } from '../../../../../../../../common/enums/dtoState';

// Hooks
import { useCriteriaStorePredicate } from '../../../../../../state/criteria/hooks/useCriteriaStorePredicate';
import { useResourceOptions } from '../../../../../../api/hooks/useResourceOptions';
import { useCriteriaStoreUpdatePredicate } from '../../../../../../state/criteria/hooks/useCriteriaStoreUpdatePredicate';
import { useDebouncedStringCallback } from '../../../../../hooks/useDebouncedStringCallback';

const FieldContainer = styled.div`
    width: 12rem;
`;

export const PredicateGroupFunctionOperator = ({ predicateUuid, plainrender = true }) => {
    const { sessionToken } = React.useContext(CriteriaStoreContext);
    const { setEdited } = React.useContext(RuleEditorContext);
    const predicate = useCriteriaStorePredicate(predicateUuid);
    const updatePredicateElement = useCriteriaStoreUpdatePredicate();
    const resourceOptionsQuery = useResourceOptions({
        sessionToken,
        resourceId: predicate?.groupFunctionOperatorDTO?.resourceId
    });
    const resourceOptions = React.useMemo(() => {
        return (resourceOptionsQuery?.data ?? []).map(({ paramId, name }) => ({
            value: paramId,
            label: name
        }));
    }, [resourceOptionsQuery?.data]);
    const [inputValue, setInputValue] = useDebouncedStringCallback({
        startVal: predicate?.groupFunctionDataInput,
        debounceCallback: (newVal) => {
            setEdited(true);
            updatePredicateElement({
                ...predicate,
                dtoState: dtoState.Updated,
                groupFunctionDataInput: newVal
            });
        }
    });

    if (plainrender) {
        return (
            <PlainRenderElementsWrapper
                overridable={true}
                items={[
                    {
                        value: predicate?.groupFunctionOperatorParamDTO?.name,
                        placeholderPartial: 'group function operator'
                    },
                    { value: inputValue, placeholderPartial: 'group function value' }
                ]}
            />
        );
    }

    return resourceOptionsQuery?.isLoading ? null : (
        <>
            <FieldContainer>
                <SelectField
                    withBorder
                    menuPortalDocument
                    id={`predicate-${predicateUuid}__group-function-operator`}
                    name={`predicate-${predicateUuid}__group-function-operator`}
                    placeholder="..."
                    tabIndex="0"
                    size={`xs`}
                    value={
                        predicate?.groupFunctionOperatorParamDTO?.paramId
                            ? resourceOptions.filter(
                                  (option) => option.value === predicate?.groupFunctionOperatorParamDTO?.paramId
                              )
                            : undefined
                    }
                    // error={errors?.dataInputNodeDTO || undefined}
                    options={resourceOptions}
                    onChange={(selectedOption) => {
                        setEdited(true);
                        updatePredicateElement({
                            ...predicate,
                            dtoState: dtoState.Updated,
                            groupFunctionOperatorParamDTO:
                                typeof predicate?.groupFunctionOperatorParamDTO !== 'undefined'
                                    ? {
                                          ...predicate.groupFunctionOperatorParamDTO,
                                          paramId: selectedOption.value,
                                          name: selectedOption.label,
                                          dtoState: dtoState.Updated
                                      }
                                    : {
                                          paramId: selectedOption.value,
                                          name: selectedOption.label,
                                          dtoState: dtoState.New
                                      }
                        });
                    }}
                />
            </FieldContainer>
            <FieldContainer>
                <TextField
                    withBorder
                    size={`xs`}
                    id={`predicate-${predicateUuid}__group-function-value`}
                    name={`predicate-${predicateUuid}__group-function-value`}
                    value={inputValue}
                    // error={errors?.nodeLabel || undefined}
                    onInput={(e) => {
                        setInputValue(e.target.value);
                    }}
                />
            </FieldContainer>
        </>
    );
};

export default PredicateGroupFunctionOperator;

PredicateGroupFunctionOperator.propTypes = {
    predicateUuid: PropTypes.string,
    plainrender: PropTypes.bool
};
